
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../context/NotificationContext';
import './UserManagement.css'; 
import UserFormModal from './UserFormModal';
import ConfirmModal from './ConfirmModal';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [compSets, setCompSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const { showNotification } = useNotification();

    const getAuthHeader = useCallback(() => {
        const token = localStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }, []);

   const fetchInitialData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const usersApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=users`;
            const compsetsApiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=compsets`;

            const [usersResponse, compSetsResponse] = await Promise.all([
                fetch(usersApiUrl, { headers: getAuthHeader() }),
                fetch(compsetsApiUrl, { headers: getAuthHeader() })
            ]);

            if (!usersResponse.ok) throw new Error(`Error fetching users: ${usersResponse.statusText}`);
            if (!compSetsResponse.ok) throw new Error(`Error fetching comp sets: ${compSetsResponse.statusText}`);
            
            const usersData = await usersResponse.json();
            const compSetsData = await compSetsResponse.json();

            if (usersData.success && Array.isArray(usersData.data)) {
                setUsers(usersData.data);
            } else {
                throw new Error(usersData.error?.message || 'Error loading users.');
            }

            if (compSetsData.success && Array.isArray(compSetsData.data)) {
                setCompSets(compSetsData.data);
            } else {
                throw new Error(compSetsData.error?.message || 'Error loading comp sets.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        fetchInitialData();
    // --- THIS IS THE FIX: Corrected the typo from 'fetchInitialTada' to 'fetchInitialData' ---
    }, [fetchInitialData]);

    const handleAddClick = () => {
        setEditingUser(null);
        setIsFormModalOpen(true);
    };

    const handleEditClick = (user) => {
        setEditingUser(user);
        setIsFormModalOpen(true);
    };

    const handleUserSaved = () => {
        fetchInitialData();
    };

    const handleDeleteRequest = (user) => {
        setUserToDelete(user);
        setIsConfirmModalOpen(true);
    };
    
    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        try {
    const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=users/${userToDelete.user_id}`;
    const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: getAuthHeader()
    });

            const result = await response.json();

            if (response.ok && result.success) {
                showNotification(result.data.message || 'Usuario eliminado exitosamente!', 'success');
                setIsConfirmModalOpen(false);
                setUserToDelete(null);
                fetchInitialData();
            } else {
                throw new Error(result.error?.message || 'Error desconocido de la API');
            }
        } catch (err) {
            showNotification(`Error al eliminar: ${err.message}`, 'error');
            setIsConfirmModalOpen(false);
            setUserToDelete(null);
        }
    };

    return (
        <>
            <div className="user-management-container">
                 <section className="info-entry-section">
                    <div className="user-list-header">
                        <h2 className="report-subtitle">Usuarios del Sistema</h2>
                        <button className="add-user-btn" onClick={handleAddClick}>+ Agregar Usuario</button>
                    </div>

                    {loading && <p>Cargando...</p>}
                    {error && <p className="error-message">{error}</p>}
                    
                    {!loading && !error && (
                        <div className="data-table-container">
                            <table className="data-input-table">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Email</th>
                                        <th>Nivel</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.user_id}>
                                            <td>{user.name}</td>
                                            <td>{user.email}</td>
                                            <td>{user.user_level}</td>
                                            <td className="action-buttons">
                                                <button className="edit-btn" onClick={() => handleEditClick(user)}>Editar</button>
                                                <button className="delete-btn" onClick={() => handleDeleteRequest(user)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>

            <UserFormModal 
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onUserSaved={handleUserSaved}
                editingUser={editingUser}
                compSets={compSets}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación"
            >
                ¿Está seguro de que desea eliminar al usuario "{userToDelete?.name}"?
            </ConfirmModal>
        </>
    );
}

export default UserManagement;